import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { environment } from '@environment';
import {
  FileBatchValidationResult,
  FileValidationResult
} from '@core/typings/file.typing';
import { AddressRequestsResources } from '@features/platform-admin/address-requests/address-requests.resources';
import { FileService, FileTypeService } from '@yourcause/common/files';
import { ConfirmAndTakeActionService } from '@yourcause/common/modals';
import { ApplicationFileResources } from '../resources/application-file.resources';

export interface ApplicationFileUrlParams {
  applicationId: number;
  applicationFormId: number;
  fileId: number;
  fileName: string;
}

@Injectable({ providedIn: 'root' })
export class ApplicationFileService {

   constructor (
    private portal: PortalDeterminationService,
    private fileService: FileService,
    private addressRequestResources: AddressRequestsResources,
    private fileTypeService: FileTypeService,
    private confirmAndTakeAction: ConfirmAndTakeActionService,
    private applicationFileResources: ApplicationFileResources
  ) { }

  convertParamsToApplicationFileUrl (
    applicationId: number,
    applicationFormId: number,
    fileId: number,
    fileName: string
  ) {
    const forApplicant = this.portal.isApply;
    const locationBase = environment.isLocalhost ?
      'yourcausegrantsqa.com' :
      location.hostname;

    const queryParams = {
      applicationId,
      applicationFormId,
      fileId,
      fileName
    };
    const query = Object.keys(queryParams)
      .map(key => key as keyof typeof queryParams)
      .map((key: keyof typeof queryParams) => `${key}=${encodeURIComponent(queryParams[key])}`)
      .join('&');

    return `https://${locationBase}/${forApplicant ? 'apply' : 'management'}/download-file?${query}`;
  }

  async doUploadFile (
    applicationId: number,
    applicationFormId: number,
    file: File,
    fileName: string,
    refFieldId: number
  ) {
    const fileId = await this.applicationFileResources.uploadFile(
      applicationId,
      applicationFormId,
      refFieldId,
      file,
      fileName
    );

    return this.convertParamsToApplicationFileUrl(
      applicationId,
      applicationFormId,
      fileId,
      fileName
    );
  }

  async uploadFile (
    applicationId: number,
    applicationFormId: number,
    file: File,
    fileName: string,
    refFieldId: number
  ) {
    const {
      passed,
      endpointResponse,
      error
    } = await this.confirmAndTakeAction.genericTakeAction(
      () => this.doUploadFile(
        applicationId,
        applicationFormId,
        file,
        fileName,
        refFieldId
      ),
      '',
      ''
    );
    if (!passed) {
      const e = error as HttpErrorResponse;
      this.fileTypeService.displayInvalidFileUploadErrorMessage(e?.error?.message, e);

      return null;
    } else {
      return endpointResponse;
    }
  }

  breakDownloadUrlDownToObject (url: string): ApplicationFileUrlParams {
    if (url) {
      const applicableInfo = url.split('?')[1];

      if (applicableInfo) {
        const info = applicableInfo.split('&')
          .map(keyValueString => keyValueString.split('='))
          .reduce((acc, keyValue) => ({
            ...acc,
            [keyValue[0]]: keyValue[1]
          }), {
            applicationId: null,
            applicationFormId: null,
            fileId: null,
            fileName: ''
          });

        let fileName = info.fileName;
        try {
          const decoded = decodeURIComponent(info.fileName);
          fileName = decoded;
        } catch (e) { }

        return {
          ...info,
          fileName
        };
      }
    }

    return null;
  }

  async openFile (
    applicationId: number,
    fileId: number,
    applicationFormId?: number
  ) {
    const blob = await this.getFileFromFileInfo(applicationFormId, applicationId, fileId);
    const blobUrl = this.fileService.convertFileToUrl(blob);

    this.doOpen(blobUrl);
  }

  doOpen (url: string) {
    window.open(url, '_blank');
  }

  async downloadFile (
    applicationId: number,
    fileId: number,
    fileName: string,
    applicationFormId?: number
  ) {
    const blob = await this.getFileFromFileInfo(applicationFormId, applicationId, fileId);

    this.fileService.saveAs(blob, fileName);
  }

  getFile (
    applicationFormId: number,
    applicationId: number,
    fileId: number
  ) {
    return this.applicationFileResources.getFile(applicationId,  applicationFormId, fileId);
  }

  /**
   * Validates a single file upload to check if it's still active on the backend.
   *
   * @param applicationId The application ID
   * @param applicationFormId The application form ID
   * @param fileUploadId The file upload ID to validate
   * @returns FileValidationResult indicating if the file is still valid
   */
  async validateFileUpload (
    applicationId: number,
    applicationFormId: number,
    fileUploadId: number
  ): Promise<FileValidationResult> {
    try {
      await this.applicationFileResources.validateFileUpload(
        applicationId,
        applicationFormId,
        fileUploadId
      );

      return {
        fileUploadId,
        isValid: true
      };
    } catch (error: any) {
      if (error?.message?.includes('does not exist or is inactive')) {
        return {
          fileUploadId,
          isValid: false,
          error: error?.message || 'File upload is no longer accessible'
        };
      }

      throw error;
    }
  }

  /**
   * Validates multiple file uploads in parallel to check if they're still active on the backend.
   * This is useful when saving forms to detect files that may have been deactivated by another session.
   *
   * @param applicationId The application ID
   * @param applicationFormId The application form ID
   * @param fileUploadIds Array of file upload IDs to validate
   * @returns FileBatchValidationResult containing valid and invalid file lists
   */
  async validateFileUploads (
    applicationId: number,
    applicationFormId: number,
    fileUploadIds: number[]
  ): Promise<FileBatchValidationResult> {
    if (!fileUploadIds || fileUploadIds.length === 0) {
      return {
        validFiles: [],
        invalidFiles: [],
        hasInvalidFiles: false
      };
    }

    const validationPromises = fileUploadIds.map((fileUploadId) =>
      this.validateFileUpload(applicationId, applicationFormId, fileUploadId)
    );

    const results = await Promise.all(validationPromises);
    const validFiles: number[] = [];
    const invalidFiles: FileValidationResult[] = [];

    results.forEach((result) => {
      if (result.isValid) {
        validFiles.push(result.fileUploadId);
      } else {
        invalidFiles.push(result);
      }
    });

    return {
      validFiles,
      invalidFiles,
      hasInvalidFiles: invalidFiles.length > 0
    };
  }

  async getFileFromFileInfo (
    applicationFormId: number,
    applicationId: number,
    fileId: number
  ) {
    let url: string;
    if (!!applicationFormId) {
      const { accessUrl } = await this.getFile(
        applicationFormId,
        applicationId,
        fileId
      );
      url = accessUrl;
    } else {
      // Only scenario is address request files
      const {
        accessUrl
      } = await this.addressRequestResources.getManagerUrlForAddressRequestFile(
        applicationId,
        fileId
      );
      url = accessUrl;
    }

    const blob = await this.fileService.getBlob(url) as File;

    return blob;
  }
}
